import { useEffect } from "react";
import LogRocket from "logrocket";
import { log } from "../utils/log";
import { isDev } from "../utils/config";
import { useSelector } from "react-redux";
import { AppState } from "../store";
import setupLogRocketReact from "logrocket-react";
import { trackTelemetryFromDobbyBackend } from "@shared/utils/telemetryApi";

let isInitialized = false;
let initStatusLogged = false;
let initTimeoutId: NodeJS.Timeout | null = null;

// Initialize LogRocket once (outside of React lifecycle)
function initializeLogRocket(appId: string) {
  if (isInitialized) return;

  log.debug("[session recording] init LogRocket");

  // Set up 10-second timeout to check initialization status
  initTimeoutId = setTimeout(() => {
    if (!initStatusLogged) {
      initStatusLogged = true;
      log.debug(
        "[session recording] LogRocket init timeout - no session URL after 10s",
      );
      trackTelemetryFromDobbyBackend({
        name: "logrocket_init_timeout",
        extra: { timeoutMs: 10000 },
      });
    }
  }, 10000);

  try {
    setupLogRocketReact(LogRocket);
    LogRocket.init(appId);
    isInitialized = true;

    // Check for immediate success
    LogRocket.getSessionURL((sessionURL) => {
      if (sessionURL && !initStatusLogged) {
        initStatusLogged = true;
        if (initTimeoutId) {
          clearTimeout(initTimeoutId);
          initTimeoutId = null;
        }
        log.debug("[session recording] LogRocket init success");
        trackTelemetryFromDobbyBackend({
          name: "logrocket_init_success",
          extra: { sessionURL },
        });
      }
    });
  } catch (error: any) {
    if (!initStatusLogged) {
      initStatusLogged = true;
      if (initTimeoutId) {
        clearTimeout(initTimeoutId);
        initTimeoutId = null;
      }
      log.debug("[session recording] LogRocket init failed", error);
      trackTelemetryFromDobbyBackend({
        name: "logrocket_init_failed",
        extra: { error: error?.message || String(error) },
      });
    }
  }
}

export function useSessionRecording(appId: string) {
  const user = useSelector(
    ({ userData }: AppState) => userData.user,
    (a, b) => a?.id === b?.id,
  );

  // Initialize LogRocket once (skip in dev mode)
  useEffect(() => {
    if (!isDev()) {
      initializeLogRocket(appId);
    }
  }, [appId]);

  // Identify user when they log in
  useEffect(() => {
    if (user && isInitialized) {
      log.debug("[session recording] set user " + user.email);
      LogRocket.identify(user?.email);
    }
  }, [user]);
}
